import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCcw, AlertTriangle } from "lucide-react";
import { isChunkLoadError, reloadOnceForChunkError } from "@/lib/chunkReload";

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Stale-deploy chunk failure → reload once onto the fresh build instead of
        // showing the error. Guarded so a genuinely-broken chunk still surfaces.
        if (isChunkLoadError(error?.message) && reloadOnceForChunkError()) return;
        console.error("Uncaught error:", error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                    <div className="max-w-md w-full text-center space-y-6 bg-white p-8 rounded-xl shadow-lg border border-gray-100">
                        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        </div>

                        <div className="space-y-2">
                            <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
                            <p className="text-gray-500">
                                We apologize for the inconvenience. The application encountered an unexpected error.
                            </p>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-lg text-left text-sm font-mono text-gray-600 overflow-auto max-h-32 border border-gray-200">
                            {this.state.error?.message || "Unknown error occurred"}
                        </div>

                        <Button
                            onClick={() => window.location.reload()}
                            className="w-full"
                            variant="default"
                        >
                            <RefreshCcw className="w-4 h-4 mr-2" />
                            Reload Application
                        </Button>

                        <p className="text-xs text-gray-400">
                            If this persists, please contact support at hello@frayze.ca
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
